import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, FileCode, Hash, AlertTriangle, RefreshCw, Lock, AlertCircle, Clock, Cpu, Server, Zap, Database, Activity, Network, ArrowUpRight, Menu, X, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { sendMessage } from "../api/openai";

interface Message {
  id: string;
  role: 'system' | 'user' | 'assistant';
  content: string;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'error';
  category?: 'security-threat' | 'hash-decode' | 'malware-analysis' | 'general';
}

interface SystemStatus {
  status: 'operational' | 'degraded' | 'maintenance' | 'offline';
  responseTime: number;
  uptime: number;
  lastChecked: Date;
  subsystems: {
    api: 'operational' | 'degraded' | 'offline';
    database: 'operational' | 'degraded' | 'offline';
    ml: 'operational' | 'degraded' | 'offline';
    security: 'operational' | 'degraded' | 'offline';
  };
}

const ShieldxAI = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: "Welcome to ShieldX AI. I can help you with the latest security threats, hash decoding, malware analysis, and provide expert information on cybersecurity and information security. How can I assist you today?",
      timestamp: new Date(),
      category: 'general'
    }
  ]);
  
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeFeature, setActiveFeature] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [systemPulse, setSystemPulse] = useState(0);
  const [mobileAIFeaturesOpen, setMobileAIFeaturesOpen] = useState(false);
  
  // Enhanced system status tracking
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    status: 'operational',
    responseTime: 124,
    uptime: 99.98,
    lastChecked: new Date(),
    subsystems: {
      api: 'operational',
      database: 'operational',
      ml: 'operational',
      security: 'operational'
    }
  });
  
  // Simulate dynamic system status updates
  useEffect(() => {
    const statusInterval = setInterval(() => {
      // Simulate response time variations
      setSystemStatus(prev => ({
        ...prev,
        responseTime: Math.max(80, Math.min(250, prev.responseTime + (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 15))),
        lastChecked: new Date()
      }));
      
      // Pulse animation for system activity
      setSystemPulse(prev => (prev + 1) % 3);
    }, 3000);
    
    return () => clearInterval(statusInterval);
  }, []);

  // Responsive design - collapse features on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileAIFeaturesOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate a unique ID
  const generateId = (): string => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  // Scroll to bottom of chat
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Function to generate a fallback response when API fails
  const generateFallbackResponse = (query: string): string => {
    // Simple fallback response system
    if (query.toLowerCase().includes('hash')) {
      return "I'm currently unable to decode this hash due to connectivity issues. For hash decoding, you would typically need to identify the hash type first (MD5, SHA1, etc.) and then use appropriate tools or algorithms to attempt decoding it.";
    } else if (query.toLowerCase().includes('malware') || query.toLowerCase().includes('virus')) {
      return "While I can't analyze this specific case right now due to connectivity issues, malicious software typically exhibits behaviors like persistence mechanisms, obfuscation techniques, command-and-control communication, and evasion tactics to avoid detection.";
    } else if (query.toLowerCase().includes('threat')) {
      return "I'm currently unable to retrieve the latest threat data. Some recent significant threats include ransomware campaigns targeting critical infrastructure, supply chain attacks, and zero-day exploits in popular software. Please check reputable sources like CISA, US-CERT, or MITRE for the latest advisories.";
    } else if (query.toLowerCase().includes('2025') || query.toLowerCase().includes('latest')) {
      return "I apologize, but I'm currently unable to access the latest 2025 cybersecurity data due to connectivity issues. When online, I can provide information on current threats, vulnerabilities, and security trends up through 2025.";
    } else {
      return "I apologize, but I'm currently experiencing connectivity issues. Please try again later, or refer to trusted cybersecurity resources like NIST, SANS, or OWASP for guidance on your security concerns.";
    }
  };

  // Check server status
  const checkServerStatus = async (): Promise<boolean> => {
    try {
      const response = await fetch('/api/status');
      return response.ok;
    } catch (error) {
      console.error('Error checking server status:', error);
      return false;
    }
  };

  // Send message to AI
  const handleSendMessage = async (content = input, category: Message['category'] = 'general') => {
    if (!content.trim()) return;
    
    // Create and add user message
    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: new Date(),
      category
    };
    
    // Create a placeholder for the assistant's response
    const assistantMessageId = generateId();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      status: 'sending',
      category
    };
    
    setMessages(prevMessages => [...prevMessages, userMessage, assistantMessage]);
    setInput('');
    setIsLoading(true);
    
    // Check server status before sending message
    const serverAvailable = await checkServerStatus();
    
    // Update system status based on server availability
    if (!serverAvailable) {
      setSystemStatus(prev => ({
        ...prev,
        status: 'degraded',
        subsystems: {
          ...prev.subsystems,
          api: 'offline'
        }
      }));
    }
    
    try {
      console.log("Sending message to OpenAI API");
      
      // Measure response time
      const startTime = Date.now();
      
      // Call the OpenAI API using the imported sendMessage function
      const responseText = await sendMessage(content);
      
      // Calculate response time
      const responseTime = Date.now() - startTime;
      
      console.log("Received response from OpenAI API");
      
      // Update system status with response time
      setSystemStatus(prev => ({
        ...prev,
        status: 'operational',
        responseTime: Math.floor(responseTime),
        subsystems: {
          ...prev.subsystems,
          api: 'operational'
        }
      }));
      
      // Update the assistant message with the response
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === assistantMessageId 
            ? { ...msg, content: responseText, status: 'sent' } 
            : msg
        )
      );
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Update system status to degraded
      setSystemStatus(prev => ({
        ...prev,
        status: 'degraded',
        subsystems: {
          ...prev.subsystems,
          api: 'degraded'
        }
      }));
      
      // Generate a fallback response
      const fallbackResponse = generateFallbackResponse(content);
      
      // Update assistant message to show the fallback response
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === assistantMessageId 
            ? { ...msg, content: fallbackResponse, status: 'error' } 
            : msg
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    handleSendMessage();
  };

  const handleFeatureClick = (feature: string, query: string) => {
    setActiveFeature(feature);
    setMobileAIFeaturesOpen(false);
    let category: Message['category'] = 'general';
    
    switch(feature) {
      case 'security-threat':
        category = 'security-threat';
        break;
      case 'hash-decode':
        category = 'hash-decode';
        break;
      case 'malware-analysis':
        category = 'malware-analysis';
        break;
      default:
        category = 'general';
    }
    
    handleSendMessage(query, category);
  };

  // Get status color based on system status
  const getStatusColor = (status: SystemStatus['status'] | keyof SystemStatus['subsystems']) => {
    switch (status) {
      case 'operational':
        return 'text-green-500';
      case 'degraded':
        return 'text-yellow-500';
      case 'maintenance':
        return 'text-blue-500';
      case 'offline':
        return 'text-red-500';
      default:
        return 'text-green-500';
    }
  };

  // Get status circle color based on system status
  const getStatusCircleColor = (status: SystemStatus['status'] | keyof SystemStatus['subsystems']) => {
    switch (status) {
      case 'operational':
        return '#22c55e'; // green-500
      case 'degraded':
        return '#eab308'; // yellow-500
      case 'maintenance':
        return '#3b82f6'; // blue-500
      case 'offline':
        return '#ef4444'; // red-500
      default:
        return '#22c55e';
    }
  };

  // Format response time display
  const formatResponseTime = (ms: number) => {
    if (ms < 100) return 'Excellent';
    if (ms < 200) return 'Good';
    if (ms < 350) return 'Average';
    return 'Slow';
  };

  // Get subsystem icon
  const getSubsystemIcon = (system: string) => {
    switch (system) {
      case 'api':
        return <Network className="h-4 w-4" />;
      case 'database':
        return <Database className="h-4 w-4" />;
      case 'ml':
        return <Cpu className="h-4 w-4" />;
      case 'security':
        return <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  return (
    <div className="container mx-auto px-3 sm:px-6 py-4 sm:py-8">
      {/* HUD-Style Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-10 p-1 border border-red-900/30 rounded-md bg-black/30 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 sm:py-0 sm:gap-4 sm:px-4">
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">AI:</span> ACTIVE
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">MODE:</span> ENHANCED
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">SECURITY:</span> LEVEL 5
          </div>
        </div>
        <div className="flex items-center px-2 py-2 sm:py-0">
          <div className="h-2 bg-gradient-to-r from-red-900 via-red-600 to-red-900 animate-pulse mx-2 sm:mx-4" style={{ width: '60px', maxWidth: '25vw' }}></div>
          <div className="text-red-400 font-mono text-xs px-2 sm:px-4">
            <span className="text-slate-500">TIME:</span> {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Page Header with Description and Status */}
      <div className="mb-6 sm:mb-10">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="max-w-xl">
            <div className="relative">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1">
                Shield<span className="text-red-500">X</span> <span className="text-red-500">AI</span>
              </h1>
              <div className="absolute -top-2 -right-2 w-2 h-2 bg-red-600 rounded-full animate-ping"></div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-red-400 text-xs sm:text-base mb-4">
              <Bot className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Advanced Cybersecurity Assistant</span>
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 hidden sm:block"></span>
              <span className="hidden sm:inline">AI-Powered Threat Intelligence</span>
            </div>
            <p className="text-slate-300 text-xs sm:text-sm border-l-4 border-red-900/50 pl-3 pr-4 py-2 bg-black/30 rounded-r-md mb-4 sm:mb-6">
              ShieldX AI provides comprehensive threat intelligence, vulnerability assessment, and malware analysis capabilities. 
              Our advanced security engine leverages GPT-4o technology to deliver real-time insights and actionable 
              recommendations for your cybersecurity challenges.
            </p>

            {/* Mobile AI Features toggle button */}
            <div className="sm:hidden mb-4">
              <button 
                onClick={() => setMobileAIFeaturesOpen(!mobileAIFeaturesOpen)}
                className="w-full flex items-center justify-between p-3 rounded-lg bg-slate-950/70 border border-slate-800 text-white"
              >
                <div className="flex items-center">
                  <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-4 w-4 mr-2" />
                  <span className="font-medium">AI Features</span>
                </div>
                {mobileAIFeaturesOpen ? (
                  <X className="h-4 w-4 text-slate-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                )}
              </button>
            </div>

            {/* Mobile AI Features dropdown */}
            {mobileAIFeaturesOpen && (
              <div className="sm:hidden mb-4 bg-black/80 border border-slate-800 rounded-lg p-3 space-y-2">
                <button 
                  className={`w-full p-2 rounded-lg border flex items-center space-x-2 transition-all ${activeFeature === 'security-threat' ? 'bg-red-900/20 border-red-600/50 text-white' : 'bg-black/30 border-slate-800 text-slate-300'}`}
                  onClick={() => handleFeatureClick('security-threat', 'What are the latest security threats in 2025?')}
                >
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <span>Security Threat Intelligence</span>
                </button>
                
                <button 
                  className={`w-full p-2 rounded-lg border flex items-center space-x-2 transition-all ${activeFeature === 'hash-decode' ? 'bg-red-900/20 border-red-600/50 text-white' : 'bg-black/30 border-slate-800 text-slate-300'}`}
                  onClick={() => handleFeatureClick('hash-decode', 'Please decode this hash: e10adc3949ba59abbe56e057f20f883e')}
                >
                  <Hash className="h-4 w-4 text-red-500" />
                  <span>Hash Decoder</span>
                </button>
                
                <button 
                  className={`w-full p-2 rounded-lg border flex items-center space-x-2 transition-all ${activeFeature === 'malware-analysis' ? 'bg-red-900/20 border-red-600/50 text-white' : 'bg-black/30 border-slate-800 text-slate-300'}`}
                  onClick={() => handleFeatureClick('malware-analysis', 'Can you analyze this suspicious file behavior? It creates persistent registry keys, has encrypted network communication, and attempts to disable security tools.')}
                >
                  <FileCode className="h-4 w-4 text-red-500" />
                  <span>Malware Analysis</span>
                </button>
                
                <button 
                  className={`w-full p-2 rounded-lg border flex items-center space-x-2 transition-all ${activeFeature === 'cybersecurity-expertise' ? 'bg-red-900/20 border-red-600/50 text-white' : 'bg-black/30 border-slate-800 text-slate-300'}`}
                  onClick={() => handleFeatureClick('cybersecurity-expertise', 'What are the best practices for enterprise cybersecurity in 2025?')}
                >
                  <Lock className="h-4 w-4 text-red-500" />
                  <span>Cybersecurity Expertise</span>
                </button>
              </div>
            )}

            {/* Desktop AI Features section */}
            <div className="hidden sm:block bg-slate-950/70 border border-slate-800 rounded-lg p-4 backdrop-blur-sm hover:border-red-900/30 transition-all">
              <h3 className="text-lg font-bold mb-4 flex items-center">
                <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-5 w-5 mr-2" />
                AI Features
              </h3>
              
              <div className="grid grid-cols-2 gap-3">
                <button 
                  className={`p-3 rounded-lg border flex items-center space-x-3 transition-all ${activeFeature === 'security-threat' ? 'bg-red-900/20 border-red-600/50 text-white' : 'bg-black/30 border-slate-800 text-slate-300 hover:border-red-900/30'}`}
                  onClick={() => handleFeatureClick('security-threat', 'What are the latest security threats in 2025?')}
                >
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <span>Security Threat Intelligence</span>
                </button>
                
                <button 
                  className={`p-3 rounded-lg border flex items-center space-x-3 transition-all ${activeFeature === 'hash-decode' ? 'bg-red-900/20 border-red-600/50 text-white' : 'bg-black/30 border-slate-800 text-slate-300 hover:border-red-900/30'}`}
                  onClick={() => handleFeatureClick('hash-decode', 'Please decode this hash: e10adc3949ba59abbe56e057f20f883e')}
                >
                  <Hash className="h-5 w-5 text-red-500" />
                  <span>Hash Decoder</span>
                </button>
                
                <button 
                  className={`p-3 rounded-lg border flex items-center space-x-3 transition-all ${activeFeature === 'malware-analysis' ? 'bg-red-900/20 border-red-600/50 text-white' : 'bg-black/30 border-slate-800 text-slate-300 hover:border-red-900/30'}`}
                  onClick={() => handleFeatureClick('malware-analysis', 'Can you analyze this suspicious file behavior? It creates persistent registry keys, has encrypted network communication, and attempts to disable security tools.')}
                >
                  <FileCode className="h-5 w-5 text-red-500" />
                  <span>Malware Analysis</span>
                </button>
                
                <button 
                  className={`p-3 rounded-lg border flex items-center space-x-3 transition-all ${activeFeature === 'cybersecurity-expertise' ? 'bg-red-900/20 border-red-600/50 text-white' : 'bg-black/30 border-slate-800 text-slate-300 hover:border-red-900/30'}`}
                  onClick={() => handleFeatureClick('cybersecurity-expertise', 'What are the best practices for enterprise cybersecurity in 2025?')}
                >
                  <Lock className="h-5 w-5 text-red-500" />
                  <span>Cybersecurity Expertise</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Enhanced AI Status Component - Desktop Only */}
          <div className="hidden md:block bg-black/40 rounded-xl border border-red-900/30 p-0 backdrop-blur-sm overflow-hidden relative group hover:border-red-600/50 transition-all shadow-lg shadow-black/20 hover:shadow-red-900/10">
            {/* Top status bar */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent opacity-30 group-hover:opacity-80 transition-opacity"></div>
            
            {/* Corner highlights */}
            <div className="absolute top-0 left-0 w-16 h-16 overflow-hidden">
              <div className="absolute top-0 left-0 w-8 h-1 bg-red-700 opacity-30 group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute top-0 left-0 h-8 w-1 bg-red-700 opacity-30 group-hover:opacity-100 transition-opacity"></div>
            </div>
            <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
              <div className="absolute top-0 right-0 w-8 h-1 bg-red-700 opacity-30 group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute top-0 right-0 h-8 w-1 bg-red-700 opacity-30 group-hover:opacity-100 transition-opacity"></div>
            </div>
            
            {/* Main status display */}
            <div className="p-4 border-b border-slate-800/70">
              <div className="flex items-center space-x-4">
                <div>
                  <div className="text-xs text-slate-400 mb-1">AI System Status</div>
                  <div className={`text-2xl font-bold ${getStatusColor(systemStatus.status)} flex items-center space-x-2`}>
                    <span>
                      {systemStatus.status === 'operational' ? 'OPERATIONAL' : 
                       systemStatus.status === 'degraded' ? 'DEGRADED PERFORMANCE' :
                       systemStatus.status === 'maintenance' ? 'MAINTENANCE MODE' : 'OFFLINE'}
                    </span>
                    <div className={`h-2 w-2 rounded-full ${getStatusColor(systemStatus.status)} animate-pulse`}></div>
                  </div>
                </div>
                <div className="relative h-16 w-16">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                    {/* Background circle */}
                    <circle cx="18" cy="18" r="16" fill="none" stroke="#27272a" strokeWidth="2"></circle>
                    
                    {/* Status circle */}
                    <circle
                      cx="18" cy="18" r="16"
                      fill="none" stroke={getStatusCircleColor(systemStatus.status)} strokeWidth="2"
                      strokeDasharray="100"
                      strokeDashoffset="0"
                      strokeLinecap="round">
                    </circle>
                    
                    {/* Pulse effect */}
                    <circle
                      cx="18" cy="18" r={16 + systemPulse * 2}
                      fill="none" stroke={getStatusCircleColor(systemStatus.status)} strokeWidth="1"
                      strokeOpacity={0.7 - systemPulse * 0.2}
                      strokeDasharray="100"
                      strokeDashoffset="0"
                      strokeLinecap="round">
                    </circle>
                  </svg>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-base font-bold">UP</div>
                </div>
              </div>
            </div>
            
            {/* System metrics */}
            <div className="grid grid-cols-3 divide-x divide-slate-800/70 border-b border-slate-800/70">
              <div className="p-3 text-center">
                <div className="text-xs text-slate-400 mb-1">Response</div>
                <div className={`text-sm font-bold ${systemStatus.responseTime < 150 ? 'text-green-500' : systemStatus.responseTime < 250 ? 'text-yellow-500' : 'text-red-500'}`}>
                  {systemStatus.responseTime}ms
                </div>
                <div className="text-xs text-slate-500">{formatResponseTime(systemStatus.responseTime)}</div>
              </div>
              <div className="p-3 text-center">
                <div className="text-xs text-slate-400 mb-1">Uptime</div>
                <div className="text-sm font-bold text-green-500">{systemStatus.uptime.toFixed(2)}%</div>
                <div className="text-xs text-slate-500">Last 30 days</div>
              </div>
              <div className="p-3 text-center">
                <div className="text-xs text-slate-400 mb-1">Model</div>
                <div className="text-sm font-bold text-red-500">ShieldX G1</div>
                <div className="text-xs text-slate-500">Advanced</div>
              </div>
            </div>
            
            {/* Subsystems status */}
            <div className="p-3">
              <div className="text-xs text-slate-400 mb-2 px-1">Subsystems</div>
              <div className="space-y-1.5">
                {Object.entries(systemStatus.subsystems).map(([key, status]) => (
                  <div key={key} className="flex items-center justify-between px-1">
                    <div className="flex items-center">
                      <div className={`p-1 rounded-md ${getStatusColor(status)} bg-slate-900/80`}>
                        {getSubsystemIcon(key)}
                      </div>
                      <span className="text-xs font-medium ml-2 capitalize">{key}</span>
                    </div>
                    <div className="flex items-center">
                      <span className={`h-1.5 w-1.5 rounded-full ${getStatusColor(status)} mr-1.5 ${status === 'operational' ? 'animate-pulse' : ''}`}></span>
                      <span className={`text-xs ${getStatusColor(status)}`}>
                        {status === 'operational' ? 'Online' : 
                         status === 'degraded' ? 'Degraded' : 
                         status === 'maintenance' ? 'Maintenance' : 'Offline'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-center">
                <div className="text-xs text-slate-500 flex items-center justify-center">
                  <Clock className="h-3 w-3 mr-1 text-slate-400" />
                  <span>Last updated: {systemStatus.lastChecked.toLocaleTimeString()}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile status card (simplified) */}
          <div className="sm:hidden bg-black/40 rounded-xl border border-red-900/30 p-3 backdrop-blur-sm w-full">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-400 mb-1">AI System Status</div>
                <div className={`text-xl font-bold ${getStatusColor(systemStatus.status)} flex items-center`}>
                  <span className={`h-2 w-2 rounded-full ${getStatusColor(systemStatus.status)} animate-pulse mr-2`}></span>
                  <span>{systemStatus.status.toUpperCase()}</span>
                </div>
              </div>
              <div className={`text-sm ${systemStatus.responseTime < 150 ? 'text-green-500' : systemStatus.responseTime < 250 ? 'text-yellow-500' : 'text-red-500'} flex flex-col items-end`}>
                <span className="text-xs text-slate-400">Response Time</span>
                <span className="font-bold">{systemStatus.responseTime}ms</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Interface - Full width */}
      <div className="bg-slate-950/70 border border-slate-800 rounded-lg backdrop-blur-sm overflow-hidden flex flex-col h-[450px] sm:h-[600px]">
        {/* Chat Header */}
        <div className="p-3 sm:p-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 bg-red-900/30 rounded-lg border border-red-900/40 mr-3">
              <Bot className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
            </div>
            <div>
              <h3 className="font-bold text-sm sm:text-base">ShieldX AI Assistant</h3>
              <div className="flex items-center text-xs sm:text-sm text-slate-400">
                <span className={`h-1.5 w-1.5 rounded-full ${getStatusColor(systemStatus.status)} animate-pulse mr-1.5`}></span>
                <span>{systemStatus.status === 'operational' ? 'Online' : systemStatus.status === 'degraded' ? 'Degraded' : systemStatus.status === 'maintenance' ? 'Maintenance' : 'Offline'}</span>
                <span className="mx-2">•</span>
                <span>Ready to assist</span>
              </div>
            </div>
          </div>
          
          <div className="hidden sm:flex items-center">
            <div className="bg-black/40 px-3 py-1.5 rounded-md border border-slate-800 text-xs text-slate-400 flex items-center">
              <Cpu className="h-3 w-3 mr-1 text-red-500" />
              <span>G1 Powered</span>
            </div>
          </div>
        </div>
        
        {/* Chat Messages */}
        <div className="flex-grow overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
          {messages.map((message) => (
            <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] sm:max-w-3/4 ${message.role === 'user' ? 'bg-red-900/20 border border-red-900/40' : 'bg-slate-900/80 border border-slate-800'} rounded-lg p-2 sm:p-3 ${message.status === 'error' ? 'border-yellow-600/50' : ''}`}>
                <div className="flex items-start space-x-2">
                  {message.role === 'assistant' ? (
                    <div className="p-1 sm:p-1.5 bg-black/40 rounded-lg border border-red-900/30 hidden sm:block">
                      <Bot className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                    </div>
                  ) : (
                    <div className="p-1 sm:p-1.5 bg-black/40 rounded-lg border border-red-900/30 hidden sm:block">
                      <User className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                    </div>
                  )}
                  <div className="w-full sm:w-auto">
                    <div className="flex items-center flex-wrap gap-1 sm:gap-2">
                      <p className="text-xs sm:text-sm font-medium">
                        {message.role === 'assistant' ? 'ShieldX AI' : 'You'}
                      </p>
                      {message.category && message.role === 'assistant' && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          message.category === 'security-threat' ? 'bg-red-900/30 text-red-400 border border-red-900/30' : 
                          message.category === 'hash-decode' ? 'bg-blue-900/30 text-blue-400 border border-blue-900/30' :
                          message.category === 'malware-analysis' ? 'bg-orange-900/30 text-orange-400 border border-orange-900/30' :
                          'bg-purple-900/30 text-purple-400 border border-purple-900/30'
                        }`}>
                          {message.category === 'security-threat' ? 'Threat Intel' : 
                            message.category === 'hash-decode' ? 'Hash Analysis' :
                            message.category === 'malware-analysis' ? 'Malware Analysis' :
                            'General'
                          }
                        </span>
                      )}
                    </div>
                    <div className="mt-1">
                      {message.status === 'sending' ? (
                        <div className="flex items-center text-slate-400">
                          <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse mr-1"></div>
                          <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse mr-1" style={{ animationDelay: '0.2s' }}></div>
                          <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse mr-1" style={{ animationDelay: '0.4s' }}></div>
                          <span className="text-xs ml-1">Analyzing...</span>
                        </div>
                      ) : message.status === 'error' ? (
                        <div className="flex flex-col">
                          <div className="flex items-center text-yellow-500 mb-1">
                            <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="text-xs">Connectivity issues - providing local response</span>
                          </div>
                          <p className="whitespace-pre-wrap text-xs sm:text-sm">{message.content}</p>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap text-xs sm:text-sm">{message.content}</p>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input Area */}
        <div className="p-3 sm:p-4 border-t border-slate-800">
          <form onSubmit={handleSubmit} className="flex items-center space-x-2 sm:space-x-3">
            <div className="flex-grow relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="w-full bg-black/40 border border-slate-800 rounded-lg py-2 sm:py-3 px-3 sm:px-4 text-sm sm:text-base text-white placeholder:text-slate-500 focus:outline-none focus:border-red-900/50"
                disabled={isLoading}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className={`p-2 sm:p-3 rounded-lg ${
                isLoading || !input.trim() ? 'bg-slate-800 text-slate-500' : 'bg-red-900/80 text-white hover:bg-red-800'
              } transition-colors`}
            >
              {isLoading ? (
                <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
              ) : (
                <Send className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>
          </form>
          <div className="mt-2 flex flex-wrap gap-2">
            <button 
              className="text-xs bg-slate-900/60 text-slate-400 py-1 px-2 rounded border border-slate-800 hover:border-red-900/30 transition-colors"
              onClick={() => setInput("What are the latest cyber threats in 2025?")}
            >
              2025 threats
            </button>
            <button 
              className="text-xs bg-slate-900/60 text-slate-400 py-1 px-2 rounded border border-slate-800 hover:border-red-900/30 transition-colors"
              onClick={() => setInput("How can I protect against ransomware?")}
            >
              Ransomware protection
            </button>
            <button 
              className="text-xs bg-slate-900/60 text-slate-400 py-1 px-2 rounded border border-slate-800 hover:border-red-900/30 transition-colors"
              onClick={() => setInput("What are the most sophisticated attack techniques in 2025?")}
            >
              Advanced attacks
            </button>
            <button 
              className="text-xs bg-slate-900/60 text-slate-400 py-1 px-2 rounded border border-slate-800 hover:border-red-900/30 transition-colors"
              onClick={() => setInput("About AlertStrike AI SIEM which is powered by Shieldx Security")}
            >
              About AlertStrike AI SIEM
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShieldxAI;
